import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

type VersionInfo = {
  bundleHash: string | null;
  bundleMtime: string | null;
  gitSha: string | null;
  gitShaShort: string | null;
  serverBootTime: string;
};

const POLL_INTERVAL_MS = 60_000;

/** `nurBanner`: nur Abfrage + Banner „Neue Version“, ohne Abzeichen — fuer Ansichten ohne Kopfleiste (Fern-Ansicht ?dgr=1). */
export function VersionBadge({ nurBanner = false }: { nurBanner?: boolean } = {}) {
  const [boot, setBoot] = useState<VersionInfo | null>(null);
  const [latest, setLatest] = useState<VersionInfo | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      const r = await fetch('/api/version', { signal: AbortSignal.timeout(5000) });
      if (!r.ok) return null;
      return (await r.json()) as VersionInfo;
    } catch { return null; }
  }, []);

  // First fetch → boot snapshot. Then poll.
  useEffect(() => {
    let cancelled = false;
    fetchVersion().then(v => {
      if (cancelled || !v) return;
      setBoot(v);
      setLatest(v);
    });
    const id = setInterval(async () => {
      const v = await fetchVersion();
      if (cancelled || !v) return;
      setLatest(v);
    }, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [fetchVersion]);

  if (!boot) return null;

  const updateAvailable = !!(latest && boot && latest.bundleHash && latest.bundleHash !== boot.bundleHash);

  const onCopy = () => {
    const label = `${boot.gitShaShort ?? '?'} / ${boot.bundleHash ?? '?'}`;
    navigator.clipboard?.writeText(label).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const onReload = () => { window.location.reload(); };

  return (
    <>
      {/* Banner per Portal an <body>: der Kopf blendet beim Einklappen (Tablet) seine Kinder aus,
          die Meldung „Neue Version" soll davon unberuehrt bleiben. */}
      {updateAvailable && createPortal(
        <div
          role="alert"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10000,
            background: 'var(--tn-warn)',
            color: 'var(--tnf-1f2937)',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          <span>Neue Version verfügbar ({latest?.gitShaShort ?? '?'} / {latest?.bundleHash ?? '?'}). Reload um die neue Version zu laden.</span>
          <button
            data-ai-id="version-badge-reload"
            onClick={onReload}
            style={{
              background: 'var(--tnfh-1f2937)',
              color: 'var(--tn-warn)',
              border: 'none',
              padding: '4px 12px',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Reload
          </button>
        </div>,
        document.body,
      )}

      {!nurBanner && <>
      {/* Das Abzeichen steht in der Kopfleiste (ProjectTabs), nicht mehr fest unten rechts: dort lag
          es ueber den Fussleisten der Panels, u. a. ueber „Senden" im Mail-Entwurf (Rafael, 24.10.2026). */}
      <div
        data-ai-id="version-badge-copy"
        onClick={onCopy}
        title={`Click to copy. git: ${boot.gitSha ?? '?'}\nbundle: ${boot.bundleHash ?? '?'}\nbuild: ${boot.bundleMtime ?? '?'}\nserver-boot: ${boot.serverBootTime}`}
        style={{
          marginRight: 6,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          WebkitAppRegion: 'no-drag',
          background: 'var(--tn-abzeichen)',
          color: 'var(--tn-abzeichen-text)',
          padding: '2px 6px',
          borderRadius: 3,
          fontSize: 10,
          fontFamily: 'monospace',
          cursor: 'pointer',
          pointerEvents: 'auto',
          userSelect: 'none',
          border: updateAvailable ? '1px solid var(--tn-warn)' : '1px solid transparent',
        } as React.CSSProperties}
      >
        {copied ? 'copied' : `${boot.gitShaShort ?? '?'} / ${boot.bundleHash ?? '?'}`}
      </div>
      </>}
    </>
  );
}
